import queryString from "query-string"

// Helper function to normalize account_type for URLs
// Converts "individual" to "professional" in URLs, but keeps other values as-is
const normalizeAccountTypeForURL = (searchParamGroup) => {
  if (!searchParamGroup) return searchParamGroup
  
  const normalized = { ...searchParamGroup }
  if (normalized.account_type === "individual") {
    normalized.account_type = "professional"
  }
  return normalized
}

// Helper function to normalize account_type from URLs
// Converts "professional" back to "individual" for internal logic/API calls
export const normalizeAccountTypeFromURL = (accountType) => {
  return accountType === "professional" ? "individual" : accountType
}

export const paths = {
  home: "/",
  login: "/login",
  notFound: "/not-found",
  resetPassword: "/reset-password",
  signUp: (type, step) => {
    // Handle route patterns (for route definitions) - don't transform them
    if ((type && type.includes(':')) || (step && step.includes(':'))) {
      return `/sign-up${type}/${step}`
    }
    
    // Convert step to step-X format for actual navigation
    let stepValue = step
    if (typeof step === 'number') {
      stepValue = `step-${step}`
    } else if (step && typeof step === 'string' && !step.startsWith('step-')) {
      // If it's a string that's just a number, convert to step-X format
      // But preserve if it's already in step-X format
      stepValue = `step-${step}`
    }
    
    // Convert "individual" to "professional" in URL
    const typeValue = type === '/individual' ? '/professional' : type
    return `/sign-up${typeValue}/${stepValue}`
  },
  communityGuideline: "/community-guideline",
  emailVerifyMsgPage: (token) => `/verify-email/${token}`,
  emailVerifyResend: (email) => `/verify-email-resend/${email}`,
  individualProfile: "/professional-profile", // URL shows "professional" but backend still uses "individual"
  selectedUserProfile: "/user/:id",
  videoGallery: (type, id) => `/video-gallery/${type}${id}`,
  // photoGallery: "/photo-gallery/:id?",
  photoGallery: (type, id) => `/photo-gallery/${type}${id}`,
  companyProfile: "/company-profile",
  opportunityEdit: "/opportunity-edit/",
  opportunity: "/opportunity/",
  opportunityShare: (uuid) => `/opportunities/${uuid}/share`,
  opportunitySearch: (searchParamGroup) => {
    // Normalize account_type: "individual" -> "professional" in URL
    const normalizedParams = normalizeAccountTypeForURL(searchParamGroup)
    const search = queryString.stringify(normalizedParams, {
      skipEmptyString: true,
      skipNull: true,
    })
    return `/opportunity-search${search ? `?${search}` : ``}`
  },
  memberSearch: (searchParamGroup) => {
    // Normalize account_type: "individual" -> "professional" in URL
    const normalizedParams = normalizeAccountTypeForURL(searchParamGroup)
    const search = queryString.stringify(normalizedParams, {
      skipEmptyString: true,
      skipNull: true,
      arrayFormat: "comma",
    })
    return `/member-search${search ? `?${search}` : ``}`
  },
  resourceSearch: (searchParamGroup) => {
    // Normalize account_type: "individual" -> "professional" in URL
    const normalizedParams = normalizeAccountTypeForURL(searchParamGroup)
    const search = queryString.stringify(normalizedParams, {
      skipEmptyString: true,
      skipNull: true,
    })
    return `/resource-search${search ? `?${search}` : ``}`
  },
  questionSearch: (searchParamGroup) => {
    // Normalize account_type: "individual" -> "professional" in URL
    const normalizedParams = normalizeAccountTypeForURL(searchParamGroup)
    const search = queryString.stringify(normalizedParams, {
      skipEmptyString: true,
      skipNull: true,
    })
    return `/community-search${search ? `?${search}` : ``}`
  },
  instituteSearch: (searchParamGroup) => {
    const search = queryString.stringify(searchParamGroup, {
      skipEmptyString: true,
      skipNull: true,
    })
    return `/institute-search${search ? `?${search}` : ``}`
  },
  inspirationSearch: (searchParamGroup) => {
    const search = queryString.stringify(searchParamGroup, {
      skipEmptyString: true,
      skipNull: true,
    })
    return `/inspiration-search${search ? `?${search}` : ``}`
  },
  opportunityPage: (uuid, from) =>
    `/opportunity?id=${uuid}${from ? `&from=${from}` : ""}`,
  departments: (uuid) => `/departments/${uuid}`,
  opportunities: (uuid) => `/opportunities/${uuid}`,
  opportunitiesAll: (uuid) => `/opportunities/${uuid}/all`,
  companyResources: (uuid) => `/company/${uuid}/resources`,
  companyQuestions: (uuid) => `/company/${uuid}/questions`,
  myEvents: "/resources/my-events",
  resourcePage: (resourceID) => `/resources/${resourceID}`,
  resourceAttendees: (resourceID) => `/resources/${resourceID}/attendees`,
  selectedCopmanyProfile: (uuid, from) =>
    `/company/${uuid}${from ? `?from=${from}` : ""}`,
  selectedIndividualProfile: (uuid) => `/user/${uuid}`,
  /** id = Upwork profile_key (ciphertext) for GET /freelancer/:id; fallback to external_id */
  selectedFreelancerProfile: (id) => `/freelancer/${id}`,
  prospects: "/prospects",
  chats: (uuid) => `/chats/${uuid}`,
  resourcesEdit: "/resources-edit/",
  communityMyQuestions: "/community/my-questions",
  communityTopic: (uuid) => `/community/topics/${uuid}`,
  communityTopicAccept: (uuid) => `/community/topics/${uuid}/accept`,
  communityTopicDecline: (uuid) => `/community/topics/${uuid}/decline`,
  communityTopicEdit: "/community-topic-edit/",
  notifications: "/notifications",
  specificInstituteArticle: (uuid) => `/institute-article/${uuid}`,
  specificInspirationArticle: (uuid) => `/inspiration-article/${uuid}`,
  accountSettings: "/settings",
  accountSettingsToken: (token) => `/settings/${token}`,
  confirmPasswordUpdate: (token) => `/confirm-password-update/${token}`,
  adminConfirmation: "/admin-confirmation",
  acceptAdminInvitation: (token) => `/accept-admin-invitation/${token}`,
  accountSubscription: "/subscription",
  notificationsSettings: "/settings/notifications",
  companyProviderLanding: "/company-provider-landing",
  individualLanding: "/individual-landing",
  emailComfirmed: "/email-comfirmed",
  employees: "/employees",
  choosePassword: "/choose-password/:token",
  favorites: "/favorites",
  reports: "/reports",
  report: (uuid) => `/reports/${uuid}`,
  adminDashboard: "/admin/dashboard",
  adminUserApplications: "/admin/user-applications",
  adminSubscriptions: "/admin/subscriptions",
  adminUserApplication: (id) => `/admin/user-application/${id}`,
  // Dashboard nested routes
  dashboardApplications: "/admin/dashboard/applications",
  dashboardSubscriptions: "/admin/dashboard/subscriptions",
  dashboardDiscountCodes: "/admin/dashboard/discount-codes",
  dashboardSubscription: (userId) => `/admin/dashboard/subscriptions/${userId}`,
  userApplication: (id) => `/user-application/${id}`,
  userApplicationSuccess: "/user-application/successfully-submit",
  paywallSignUp: "/paywall-sign-up",
  subscriptionSuccess: "/subscription-status",
}

